/**
 * 合集编辑页（创建 + 编辑）
 *
 * 一个统一的页面，通过 `mode` prop 区分：
 * - mode="create"：新建合集；先填表单 -> POST 创建 -> 跳转到 edit 模式继续加视频
 * - mode="edit" ：加载已有合集 + items；可拖拽排序、添加/删除条目
 *
 * 设计要点：
 * - 表单字段（name/description/visibility/sourceLanguage/coverUrl）改动通过 PATCH 保存
 * - 合集可见性：仅管理员可改为「公开」；普通用户界面为禁用且始终按「私有」提交
 * - 视频条目通过 POST/DELETE/PATCH-reorder 即时同步到后端（不等"保存"按钮）
 * - 拖拽排序：HTML5 原生 dragstart/dragover/drop，不引入额外依赖
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from './AppShell';
import { Select, SelectItem } from './Select';
import { Input } from './Input';
import { ConfirmDialog } from './ConfirmDialog';
import { API_BASE_URL, UPLOAD_API_BASE_URL } from '../config/api';
import { clearAuth, getAuthHeaders, getUser } from '../utils/auth';
import { resolveAssetUrl } from '../utils/assetUrl';
import { DEFAULT_COVER_PATH, handleCoverError } from '../utils/videoCover';
import type { ApiResponse, VideoDescriptor } from '../types/api';
import type { CollectionDetail, CollectionItem } from '../types/collection';
import './VideoEdit.css';
import './CollectionEdit.css';

type EditMode = 'create' | 'edit';

interface CollectionEditProps {
  mode: EditMode;
}

interface CandidateVideosResponse {
  list: VideoDescriptor[];
  page: number;
  pageSize: number;
  hasMore: boolean;
}

const LANGUAGES = [
  { code: 'ja', label: '日语' },
  { code: 'en', label: '英语' },
  { code: 'ko', label: '韩语' },
  { code: 'zh', label: '中文' },
];

const CollectionEdit: React.FC<CollectionEditProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { collectionId } = useParams<{ collectionId: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collection, setCollection] = useState<CollectionDetail | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'private' | 'public'>('private');
  const [sourceLanguage, setSourceLanguage] = useState('ja');
  const [coverUrl, setCoverUrl] = useState('');

  // 封面更换（仅 edit 模式可用）
  const coverFileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [localCoverPreview, setLocalCoverPreview] = useState<string | null>(null);

  // 拖拽相关
  const [draggingItemId, setDraggingItemId] = useState<string | null>(null);
  const [dragOverItemId, setDragOverItemId] = useState<string | null>(null);

  // 添加视频模态框 —— 候选来源：所有 public 视频（不再限于本人）
  const [addOpen, setAddOpen] = useState(false);
  const [candidatePool, setCandidatePool] = useState<VideoDescriptor[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [keyword, setKeyword] = useState('');

  // 删除条目确认
  const [removeDialog, setRemoveDialog] = useState<{ open: boolean; itemId: string | null }>({
    open: false,
    itemId: null,
  });

  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const msgTimerRef = useRef<number | null>(null);
  const flashMsg = useCallback((m: { type: 'success' | 'error'; text: string }) => {
    setMsg(m);
    if (msgTimerRef.current) {
      window.clearTimeout(msgTimerRef.current);
    }
    msgTimerRef.current = window.setTimeout(() => setMsg(null), 2500);
  }, []);

  const isAdmin = getUser()?.role === 'admin';

  // ============================================================
  // 加载已有合集
  // ============================================================

  const loadCollection = useCallback(async () => {
    if (!collectionId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/collections/${collectionId}`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as ApiResponse<CollectionDetail>;
      if (data.success && data.data) {
        setCollection(data.data);
        setName(data.data.name);
        setDescription(data.data.description || '');
        setVisibility(data.data.visibility);
        setSourceLanguage(data.data.sourceLanguage);
        setCoverUrl(data.data.coverUrl || '');
      } else {
        window.alert(data.error || '加载合集失败');
        navigate('/profile?tab=collections');
      }
    } catch (err) {
      console.error('[CollectionEdit] 加载失败:', err);
      window.alert('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [collectionId, navigate]);

  useEffect(() => {
    if (mode === 'edit') {
      loadCollection();
    } else {
      setLoading(false);
    }
  }, [mode, loadCollection]);

  // ============================================================
  // 创建合集（仅 create 模式）
  // ============================================================

  const handleCreate = async () => {
    if (!name.trim()) {
      flashMsg({ type: 'error', text: '合集名称不能为空' });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/collections`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          visibility: isAdmin ? visibility : 'private',
          sourceLanguage,
          // 封面：创建合集时不要求；建好后进入编辑页通过"更换封面"按钮上传
        }),
      });
      if (res.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      const data = (await res.json()) as ApiResponse<{ id: string }>;
      if (data.success && data.data?.id) {
        flashMsg({ type: 'success', text: '合集已创建' });
        // 跳转到编辑模式，方便用户继续添加视频
        navigate(`/collections/${data.data.id}/edit`, { replace: true });
      } else {
        flashMsg({ type: 'error', text: data.error || '创建失败' });
      }
    } catch (err) {
      console.error('[CollectionEdit] 创建失败:', err);
      flashMsg({ type: 'error', text: '网络错误，请稍后重试' });
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // 保存元信息（edit 模式）
  // ============================================================

  const handleSaveMeta = async () => {
    if (!collection) return;
    if (!name.trim()) {
      flashMsg({ type: 'error', text: '合集名称不能为空' });
      return;
    }
    const effectiveVisibility = isAdmin ? visibility : 'private';
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/collections/${collection.id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          visibility: effectiveVisibility,
          sourceLanguage,
          // 封面通过专门的 /cover 接口更新，这里不再带 coverUrl
        }),
      });
      if (res.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      const data = (await res.json()) as ApiResponse;
      if (data.success) {
        flashMsg({ type: 'success', text: '已保存' });
        setVisibility(effectiveVisibility);
        setCollection({
          ...collection,
          name: name.trim(),
          description: description.trim() || null,
          visibility: effectiveVisibility,
          sourceLanguage,
        });
      } else {
        flashMsg({ type: 'error', text: data.error || '保存失败' });
      }
    } catch (err) {
      console.error('[CollectionEdit] 保存失败:', err);
      flashMsg({ type: 'error', text: '网络错误，请稍后重试' });
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // 添加视频
  // ============================================================

  /**
   * 拉取候选视频。来源：首页同款 `/api/video-list`
   * - 后端硬约束：visibility=public + 已处理完 + 有完整双语字幕
   * - 服务端按合集学习语言过滤，少传一些数据
   *
   * 设计要点：依赖合集的 sourceLanguage，切换语言后 useCallback 会重建并
   * 在再次打开模态框时拉新数据。
   */
  const fetchCandidates = useCallback(async () => {
    if (!collection) return;
    setLoadingCandidates(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        pageSize: '100',
        sourceLanguage: collection.sourceLanguage,
      });
      const res = await fetch(`${API_BASE_URL}/video-list?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = (await res.json()) as ApiResponse<CandidateVideosResponse>;
        if (data.success && data.data) {
          setCandidatePool(data.data.list || []);
        }
      }
    } catch (err) {
      console.error('[CollectionEdit] 拉取候选视频失败:', err);
    } finally {
      setLoadingCandidates(false);
    }
  }, [collection]);

  const openAddDialog = () => {
    setAddOpen(true);
    setKeyword('');
    // 每次打开都刷新一次：合集语言可能在这期间被改过
    fetchCandidates();
  };

  const existingVideoIds = useMemo(
    () => new Set((collection?.items || []).map((it) => it.videoId)),
    [collection],
  );

  // 候选视频：服务端已按 public + 语言筛过；这里只做本地"已加入" + 关键字过滤
  const candidateVideos = useMemo(() => {
    if (!collection) return [];
    const kw = keyword.trim().toLowerCase();
    return candidatePool.filter((v) => {
      if (existingVideoIds.has(v.videoId)) return false;
      if (kw && !(v.name || v.videoId).toLowerCase().includes(kw)) return false;
      return true;
    });
  }, [candidatePool, existingVideoIds, collection, keyword]);

  const handleAddVideo = async (videoId: string) => {
    if (!collection) return;
    try {
      const res = await fetch(`${API_BASE_URL}/collections/${collection.id}/items`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoId }),
      });
      if (res.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      const data = (await res.json()) as ApiResponse<{ itemId: string; position: number }>;
      if (data.success && data.data) {
        // 重新拉详情以同步条目（也可乐观插入，但 reload 更稳妥）
        await loadCollection();
        flashMsg({ type: 'success', text: '已加入合集' });
      } else {
        flashMsg({ type: 'error', text: data.error || '加入失败' });
      }
    } catch (err) {
      console.error('[CollectionEdit] 添加视频失败:', err);
      flashMsg({ type: 'error', text: '网络错误' });
    }
  };

  // ============================================================
  // 删除条目
  // ============================================================

  const handleRemoveItem = async () => {
    const itemId = removeDialog.itemId;
    setRemoveDialog({ open: false, itemId: null });
    if (!collection || !itemId) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/collections/${collection.id}/items/${itemId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(),
        },
      );
      if (res.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      const data = (await res.json()) as ApiResponse;
      if (data.success) {
        setCollection({
          ...collection,
          items: collection.items.filter((it) => it.itemId !== itemId),
          itemCount: Math.max(0, collection.itemCount - 1),
        });
        flashMsg({ type: 'success', text: '已移除' });
      } else {
        flashMsg({ type: 'error', text: data.error || '移除失败' });
      }
    } catch (err) {
      console.error('[CollectionEdit] 移除条目失败:', err);
      flashMsg({ type: 'error', text: '网络错误' });
    }
  };

  // ============================================================
  // 拖拽排序
  // ============================================================

  /** 把 items 重排为新顺序，并把新的 position 一次性 PATCH 给后端 */
  const applyReorder = async (newItems: CollectionItem[]) => {
    if (!collection) return;
    // 乐观更新
    const previous = collection.items;
    const renumbered = newItems.map((it, idx) => ({ ...it, position: idx + 1 }));
    setCollection({ ...collection, items: renumbered });

    try {
      const res = await fetch(`${API_BASE_URL}/collections/${collection.id}/reorder`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(
          renumbered.map((it) => ({ itemId: it.itemId, position: it.position })),
        ),
      });
      if (res.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      const data = (await res.json()) as ApiResponse;
      if (!data.success) {
        // 回滚
        setCollection({ ...collection, items: previous });
        flashMsg({ type: 'error', text: data.error || '排序失败' });
      }
    } catch (err) {
      console.error('[CollectionEdit] 排序失败:', err);
      setCollection({ ...collection, items: previous });
      flashMsg({ type: 'error', text: '网络错误' });
    }
  };

  const handleDragStart = (itemId: string) => (e: React.DragEvent) => {
    setDraggingItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox 需要 setData 才能正常拖动
    try {
      e.dataTransfer.setData('text/plain', itemId);
    } catch {
      // ignore
    }
  };

  const handleDragOver = (itemId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverItemId !== itemId) {
      setDragOverItemId(itemId);
    }
  };

  const handleDragLeave = (itemId: string) => (_e: React.DragEvent) => {
    if (dragOverItemId === itemId) {
      setDragOverItemId(null);
    }
  };

  const handleDrop = (targetItemId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverItemId(null);
    const sourceId = draggingItemId;
    setDraggingItemId(null);
    if (!sourceId || sourceId === targetItemId || !collection) return;
    const items = collection.items.slice();
    const fromIdx = items.findIndex((it) => it.itemId === sourceId);
    const toIdx = items.findIndex((it) => it.itemId === targetItemId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = items.splice(fromIdx, 1);
    if (!moved) return;
    items.splice(toIdx, 0, moved);
    applyReorder(items);
  };

  const handleDragEnd = () => {
    setDraggingItemId(null);
    setDragOverItemId(null);
  };

  // ============================================================
  // 封面上传（仅 edit 模式可用，复用 VideoEdit 的交互）
  // ============================================================

  const handleCoverClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (mode !== 'edit') return;
    coverFileInputRef.current?.click();
  };

  const handleCoverSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !collection) return;

    const previewUrl = URL.createObjectURL(file);
    setLocalCoverPreview(previewUrl);
    setUploadingCover(true);

    try {
      const form = new FormData();
      form.append('cover', file);

      const headers = { ...(getAuthHeaders() as Record<string, string>) };
      // 让浏览器自动设置 multipart/form-data 的 boundary
      delete headers['Content-Type'];

      const resp = await fetch(
        `${UPLOAD_API_BASE_URL}/collections/${collection.id}/cover`,
        { method: 'POST', headers, body: form },
      );
      if (resp.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      const result = (await resp.json()) as ApiResponse<{ coverUrl?: string }>;
      if (result.success) {
        const newUrl = result.data?.coverUrl || coverUrl;
        // 加上时间戳避免浏览器/CDN 缓存上一张封面
        const cacheBusted = newUrl
          ? `${newUrl}${newUrl.includes('?') ? '&' : '?'}t=${Date.now()}`
          : '';
        setCoverUrl(cacheBusted);
        setCollection((prev) => (prev ? { ...prev, coverUrl: cacheBusted } : prev));
        flashMsg({ type: 'success', text: '封面已更新' });
      } else {
        setLocalCoverPreview(null);
        flashMsg({ type: 'error', text: result.error || '封面上传失败' });
      }
    } catch (err) {
      console.error('[CollectionEdit] 上传封面失败:', err);
      setLocalCoverPreview(null);
      flashMsg({ type: 'error', text: '网络错误，请稍后重试' });
    } finally {
      setUploadingCover(false);
      if (coverFileInputRef.current) coverFileInputRef.current.value = '';
    }
  };

  // ============================================================
  // 渲染
  // ============================================================

  const itemToDelete = removeDialog.itemId
    ? (collection?.items || []).find((it) => it.itemId === removeDialog.itemId) || null
    : null;

  const handleBack = () => {
    navigate('/profile?tab=collections');
  };

  const headerTitle = mode === 'create' ? '新建合集' : '编辑合集';

  const header = (
    <div className="video-edit-header app-shell-header-content">
      <button
        type="button"
        onClick={handleBack}
        className="back-button"
        title="返回"
        aria-label="返回"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      </button>
      <h1>{headerTitle}</h1>
    </div>
  );

  return (
    <AppShell header={header} footer={null}>
      <div className="collection-edit-page">
        {loading ? (
          <div className="collection-edit-loading">加载中...</div>
        ) : (
          <>
            {msg && (
              <div className={`collection-edit-msg collection-edit-msg--${msg.type}`}>
                {msg.text}
              </div>
            )}

            {mode === 'edit' && collection && (
              <div className="video-cover-section">
                <div className="video-cover-large">
                  <img
                    src={
                      localCoverPreview ||
                      (coverUrl
                        ? resolveAssetUrl(coverUrl) || DEFAULT_COVER_PATH
                        : DEFAULT_COVER_PATH)
                    }
                    alt={name || '合集封面'}
                    onError={handleCoverError}
                    onClick={handleCoverClick}
                    role="button"
                    tabIndex={0}
                  />
                  <input
                    ref={coverFileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleCoverSelected}
                  />
                  <div className="cover-edit-hint" onClick={handleCoverClick}>
                    {uploadingCover ? '上传中...' : '更换封面'}
                  </div>
                </div>
              </div>
            )}

            <section className="collection-edit-section">
              <h2 className="collection-edit-section-title">基本信息</h2>

              <div className="collection-edit-field">
                <label className="collection-edit-label">合集名称</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="为这个合集起个名字"
                  maxLength={100}
                />
              </div>

              <div className="collection-edit-field">
                <label className="collection-edit-label">简介（可选）</label>
                <textarea
                  className="collection-edit-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="一句话介绍这个合集"
                  maxLength={1000}
                  rows={3}
                />
              </div>

              <div className="collection-edit-row">
                <div className="collection-edit-field">
                  <label className="collection-edit-label">学习语言</label>
                  <Select value={sourceLanguage} onValueChange={setSourceLanguage}>
                    {LANGUAGES.map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.label}
                      </SelectItem>
                    ))}
                  </Select>
                </div>

                <div className="collection-edit-field">
                  <label className="collection-edit-label">可见性</label>
                  <Select
                    value={visibility}
                    onValueChange={(v) => setVisibility(v === 'public' ? 'public' : 'private')}
                    disabled={!isAdmin}
                  >
                    <SelectItem value="private">私有（仅自己可见）</SelectItem>
                    <SelectItem value="public">公开（所有人可见）</SelectItem>
                  </Select>
                  {!isAdmin && (
                    <p className="collection-edit-visibility-note">仅管理员可将合集设为公开</p>
                  )}
                </div>
              </div>

              <div className="form-actions">
                {mode === 'create' ? (
                  <button
                    type="button"
                    className="save-button"
                    disabled={saving}
                    onClick={handleCreate}
                  >
                    {saving ? '创建中...' : '创建合集'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="save-button"
                    disabled={saving}
                    onClick={handleSaveMeta}
                  >
                    {saving ? '保存中...' : '保存'}
                  </button>
                )}
              </div>
            </section>

            {mode === 'edit' && collection && (
              <section className="collection-edit-section">
                <div className="collection-edit-section-head">
                  <h2 className="collection-edit-section-title">
                    视频列表（{collection.items.length}）
                  </h2>
                  <button
                    type="button"
                    className="collection-edit-secondary-btn"
                    onClick={openAddDialog}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    <span>加入视频</span>
                  </button>
                </div>

                {collection.items.length === 0 ? (
                  <p className="collection-edit-empty">还没有视频，点击右上"加入视频"开始。</p>
                ) : (
                  <ul className="collection-edit-items">
                    {collection.items.map((it) => (
                      <li
                        key={it.itemId}
                        className={
                          `collection-edit-item${draggingItemId === it.itemId ? ' is-dragging' : ''}` +
                          (dragOverItemId === it.itemId ? ' is-drag-over' : '')
                        }
                        draggable
                        onDragStart={handleDragStart(it.itemId)}
                        onDragOver={handleDragOver(it.itemId)}
                        onDragLeave={handleDragLeave(it.itemId)}
                        onDrop={handleDrop(it.itemId)}
                        onDragEnd={handleDragEnd}
                      >
                        <div className="collection-edit-item__handle" aria-hidden="true">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="9" cy="6" r="1" />
                            <circle cx="9" cy="12" r="1" />
                            <circle cx="9" cy="18" r="1" />
                            <circle cx="15" cy="6" r="1" />
                            <circle cx="15" cy="12" r="1" />
                            <circle cx="15" cy="18" r="1" />
                          </svg>
                        </div>
                        <div className="collection-edit-item__pos">{it.position}</div>
                        <div className="collection-edit-item__thumb">
                          <img
                            src={it.videoCoverUrl ? resolveAssetUrl(it.videoCoverUrl) || DEFAULT_COVER_PATH : DEFAULT_COVER_PATH}
                            alt={it.videoName || it.videoId}
                            onError={handleCoverError}
                          />
                        </div>
                        <div className="collection-edit-item__meta">
                          <div className="collection-edit-item__name">
                            {it.videoName || it.videoId}
                          </div>
                          <div className="collection-edit-item__sub">
                            <span>{it.videoVisibility === 'public' ? '公开' : '私有'}</span>
                            {it.videoUsername && <span>· @{it.videoUsername}</span>}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="collection-edit-item__remove"
                          onClick={() =>
                            setRemoveDialog({ open: true, itemId: it.itemId })
                          }
                          title="从合集中移除"
                          aria-label="从合集中移除"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </>
        )}
      </div>

      {/* 添加视频弹窗 */}
      {addOpen && collection && (
        <div className="collection-edit-modal" onClick={() => setAddOpen(false)}>
          <div className="collection-edit-modal__inner" onClick={(e) => e.stopPropagation()}>
            <div className="collection-edit-modal__header">
              <h3>加入视频</h3>
              <button
                type="button"
                className="collection-edit-modal__close"
                onClick={() => setAddOpen(false)}
                aria-label="关闭"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="collection-edit-modal__hint">
              仅显示与合集学习语言（{collection.sourceLanguage}）一致的公开视频
            </div>

            <Input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="按视频名称过滤..."
            />

            <div className="collection-edit-modal__list">
              {loadingCandidates ? (
                <div className="collection-edit-modal__empty">加载中...</div>
              ) : candidateVideos.length === 0 ? (
                <div className="collection-edit-modal__empty">没有可加入的视频</div>
              ) : (
                candidateVideos.map((v) => (
                  <div key={v.videoId} className="collection-edit-modal__row">
                    <div className="collection-edit-modal__thumb">
                      <img
                        src={v.coverUrl ? resolveAssetUrl(v.coverUrl) || DEFAULT_COVER_PATH : DEFAULT_COVER_PATH}
                        alt={v.name || v.videoId}
                        onError={handleCoverError}
                      />
                    </div>
                    <div className="collection-edit-modal__rowmeta">
                      <div className="collection-edit-modal__rowname">
                        {v.name || v.videoId}
                      </div>
                      <div className="collection-edit-modal__rowsub">
                        {v.visibility === 'public' ? '公开' : '私有'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="collection-edit-primary-btn collection-edit-primary-btn--small"
                      onClick={() => handleAddVideo(v.videoId)}
                    >
                      添加
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={removeDialog.open}
        title="移除视频"
        message={`确定从合集中移除 "${itemToDelete?.videoName || itemToDelete?.videoId || '此视频'}" 吗？此操作不会删除视频本身。`}
        confirmText="移除"
        cancelText="取消"
        variant="danger"
        onConfirm={handleRemoveItem}
        onCancel={() => setRemoveDialog({ open: false, itemId: null })}
      />

    </AppShell>
  );
};

export default CollectionEdit;
